import { useQuery } from "@apollo/client";
import { ExtractionStatus, GetExtractedWasteProfileQuery } from "generated-graphql/graphql";
import { useCallback, useEffect, useState } from "react";

import { GET_EXTRACTED_WASTE_PROFILE, useExtractWasteProfileDocument } from "./api";

/**
 * How often to re-ask while a reading is in flight. The extraction is an LLM call
 * behind a job queue, so it takes seconds; asking faster spends requests without
 * telling the reviewer anything sooner.
 */
const EXTRACTION_POLL_MS = 3000;

type Reading = GetExtractedWasteProfileQuery["extractedWasteProfile"];

/**
 * What the review surface should show as the reading's state.
 *
 * A reading asked for moments ago has neither a candidate row nor a `PENDING`
 * stamp yet, so the server still reports whatever the previous attempt left —
 * nothing at all on a first read, or `FAILED` on a retry. Either would be taken
 * as the answer to the request just made, and the retry would appear to do
 * nothing.
 *
 * `awaitingSince` is the `readAt` seen when the request went out. While the
 * server still reports that same value it is still describing the old attempt,
 * so the surface shows progress; the moment `readAt` moves, the new reading has
 * landed and the server's own status takes over. Both values come from the
 * server, so nothing here depends on the two clocks agreeing.
 */
export function readingStatus(
  reading: { extractionStatus?: ExtractionStatus | null; readAt?: string | null } | null,
  awaitingSince: { readAt: string | null } | null
): ExtractionStatus | null {
  const status = reading?.extractionStatus ?? null;
  if (!awaitingSince) return status;
  const stillTheOldReading = (reading?.readAt ?? null) === awaitingSince.readAt;
  return stillTheOldReading ? ExtractionStatus.Pending : status;
}

/**
 * This extractor's reading of a document, and the ability to ask for a new one.
 *
 * Progress is reported through `status` rather than handed to the shared
 * analyzing dialog. That dialog is built for a bulk upload — its copy tells the
 * reader their documents were uploaded and asks them to review classifications
 * for each one, and it lists a row per document — none of which describes
 * reading a single sheet for a characterization, and none of it leads back to
 * the review grid.
 */
export function useProfileReading(documentId: string): {
  reading: Reading;
  /** True only until the first reading arrives, never on a later poll. */
  loading: boolean;
  status: ExtractionStatus | null;
  /** Why the reading could not be started, when that is what went wrong. */
  startError: string | null;
  requestReading: () => Promise<void>;
} {
  const [extractDocument] = useExtractWasteProfileDocument();
  const [startError, setStartError] = useState<string | null>(null);

  // Set when a reading is asked for, holding the `readAt` the server reported at
  // that moment — see `readingStatus`. Null once the new reading has landed.
  const [awaitingSince, setAwaitingSince] = useState<{ readAt: string | null } | null>(null);

  const { data, previousData, loading, startPolling, stopPolling } = useQuery(
    GET_EXTRACTED_WASTE_PROFILE,
    {
      variables: { documentId },
      fetchPolicy: "network-only",
      // Delivers each poll to the component; without it the grid would stay on
      // the state it first rendered even after the reading lands.
      notifyOnNetworkStatusChange: true,
    }
  );

  const reading = data?.extractedWasteProfile ?? null;
  const serverStatus = readingStatus(reading, awaitingSince);
  // A document this extractor has no reading of, and none in flight. The
  // resolver reports the document's own NOT_ATTEMPTED default as a null status,
  // because it is the state every document is born in rather than anything about
  // this extractor.
  const neverRead = reading != null && serverStatus == null;

  // Cleared as soon as the server reports a reading newer than the request, so a
  // second failure surfaces as a failure rather than spinning forever.
  useEffect(() => {
    if (awaitingSince && (reading?.readAt ?? null) !== awaitingSince.readAt) {
      setAwaitingSince(null);
    }
  }, [awaitingSince, reading?.readAt]);

  // Only while a reading is actually in flight, and stopped on the way out so a
  // closed dialog is not still asking.
  useEffect(() => {
    if (serverStatus === ExtractionStatus.Pending) {
      startPolling(EXTRACTION_POLL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [serverStatus, startPolling, stopPolling]);

  const requestReading = useCallback(async () => {
    setStartError(null);
    try {
      const job = await extractDocument({ variables: { documentId } });
      if (!job.data?.extractWasteProfileDocument) {
        throw new Error("The extraction job did not start.");
      }
      setAwaitingSince({ readAt: reading?.readAt ?? null });
    } catch (error) {
      // Reported as a failed reading rather than as a toast, because that is the
      // state the dialog is in and it is where the retry lives.
      setStartError(
        error instanceof Error ? error.message : "Could not start reading this document."
      );
    }
  }, [documentId, extractDocument, reading?.readAt]);

  // Opening the dialog is the request: the control that opens it says "Auto-fill
  // from this document", so asking again behind it is a second click for a
  // decision already made. Only ever fires for a document with no reading and
  // none in flight, so it cannot spend a second call on one we have read — and
  // never after a failure, which would retry in a loop.
  const [autoStarted, setAutoStarted] = useState(false);
  useEffect(() => {
    if (!neverRead || autoStarted) return;
    setAutoStarted(true);
    void requestReading();
  }, [neverRead, autoStarted, requestReading]);

  // Failing to start is a failed reading. And between asking and the server
  // reporting it, there is no status to show — without this the surface would
  // fall to its empty state and say the document holds nothing, mid-read.
  const reportedStatus = (() => {
    if (startError != null) return ExtractionStatus.Failed;
    if (autoStarted && serverStatus == null) return ExtractionStatus.Pending;
    return serverStatus;
  })();

  return {
    reading,
    // Only the first fetch. `notifyOnNetworkStatusChange` raises `loading` on
    // every poll too, and reporting those would swap the form out for a skeleton
    // every few seconds while the reviewer is reading it.
    loading: loading && previousData == null,
    status: reportedStatus,
    startError,
    requestReading,
  };
}
